import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardTitle, Col, DropdownMenu, DropdownToggle, Row, UncontrolledDropdown } from 'reactstrap';
import { UserId } from '@business/entities/account';
import { FileResponse, FolderId, FolderResponse } from '@business/entities/file';
import { FormTypes } from '@business/entities/form';
import { OrgId } from '@business/entities/organization';
import { RoleService } from '@business/services';
import { useBloc } from '@core/utils/bloc';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { Session } from '@modules/auth/session';
import { openDocumentEditorModal } from '@modules/document/DocumentEditorModal';
import { openAddEditFormModal } from '@modules/form/AddEditFormModal';
import { openFormShareModal } from '@modules/form/FormShareModal';
import { opemFormViewModal } from '@modules/form/FormViewModal';
import { tap } from 'rxjs/operators';
import { openDownloadFolderModal } from '.';
import { CBreadcrumb, CBreadcrumbItem } from '../Breadcrumb';
import { openGenericInputModal } from '../GenericInputModal';
import { openSelectUsersModal } from '../SelectUsersModal';
import { openAddFiles } from './AddFilesModal';
import { FileItem } from './FileItem';
import { openFileOrgModal } from './MoveFileOrgModal';
import { openMoveFiles } from './MoveFilesModal';
import { SearchList } from './SearchList';
import { openOrgFileShareModal } from './OrgFileShareModal';
import { HasPermission } from '@core/components/HasPermission';
import { OrgFilesBloc } from './OrgFilesBloc';
import { openAddPresentationModal } from '@modules/form/AddPresentationModal';
import { openPresentationSummaryModal } from '@modules/presentation/PresentationSummaryModal';
import { EmptySection } from '@core/components/EmptySection';
import { openAttachSystemFilesModal } from '@modules/form/AttachSystemFilesModal';
export interface OrgFilesProps {
  card?: boolean;
  orgId: OrgId;
  type: FormTypes;
  defaultFolderId?: FolderId;
  showBreadcrumb?: boolean;
  customTilte?: string;
  enableSearch?: boolean;
  cardHeight?: string;
  /**
   * Extra tiles shown in the same grid before the folder's files (e.g. the client portal's proposal
   * documents). They count as content, so "No Files" only shows when both are empty.
   */
  extraItems?: React.ReactNode[];
  /**
   * Client portal (client work order page): the "+" menu shows regardless of file permissions (clients
   * have none) but only Add Folder / Add Files / Add Design File / Add Document, and uploads hide the
   * sharing controls.
   */
  clientPortal?: boolean;
}

export const OrgFiles = memo((props: OrgFilesProps) => {
  const { card = false, orgId, defaultFolderId, showBreadcrumb = true, customTilte = null, enableSearch = true, cardHeight = null, extraItems = [], clientPortal = false } = props;
  const bloc = useBloc(OrgFilesBloc, orgId);
  const items = useObservable(bloc.items$, []);
  const searchItems = useObservable(bloc.searchItems$, []);
  const breadcrumbs = useNonNilObservable(bloc.breadcrumb$);
  const user = useObservable(Session.user$);
  const users = useObservable(bloc.users$, []);

  const showForm = useMemo(() => (user ? RoleService.isStaff(user) : false), [user]);
  const [search, setSearch] = useState('');
  const [folderId, setFolderId] = useState<string>(defaultFolderId ?? '0');
  const [defaultFolderRendered, setDefaultFolderRendered] = useState(false);
  const onSearch = useCallback(
    (search: string) => {
      bloc.search(search, folderId).subscribe();
    },
    [bloc, folderId],
  );

  const onDebouncedSearch = useMemo(() => R.debounce(onSearch, 250), [onSearch]);

  useEffect(() => {
    if (items.length > 0 && defaultFolderId && !defaultFolderRendered) {
      const item = items.find(i => i.id === defaultFolderId);
      if (item) {
        setFolderId(item.id);
        setDefaultFolderRendered(true);
      }
    }
  }, [items, defaultFolderId, defaultFolderRendered]);

  useEffect(() => {
    if (!R.isEmpty(search)) {
      onDebouncedSearch(search);
    }
  }, [search, onDebouncedSearch]);

  const breadcrumbItems: CBreadcrumbItem[] = useMemo(
    () => breadcrumbs.map(b => ({ title: b.name, onClick: () => setFolderId(b.id) })),
    [breadcrumbs],
  );

  const onAddFolder = useCallback(() => {
    openGenericInputModal({
      title: 'Create Folder',
      label: 'Folder name',
      button: 'Create',
      onSubmit: name => bloc.createFolder(orgId, name, folderId),
    });
  }, [bloc, orgId, folderId]);

  const onRename = useCallback(
    (item: FileResponse | FolderResponse) => {
      if (item.type === 'folder') {
        openGenericInputModal({
          title: 'Rename Folder',
          label: 'Folder name',
          button: 'Update',
          initialValue: item.name,
          onSubmit: name => bloc.renameFolder(item.id, name),
        });
      } else {
        openGenericInputModal({
          title: 'Rename File',
          label: 'Filename',
          button: 'Update',
          initialValue: item.name,
          onSubmit: name => bloc.renameFile(item.id, name),
        });
      }
    },
    [bloc],
  );

  const onShare = useCallback((item: FileResponse | FolderResponse) => {
    if (item.type !== 'folder' && item.fileType === 'form') {
      openFormShareModal({
        formId: item.id,
      });
    }
  }, []);

  const onShareWithClient = useCallback(
    (item: FileResponse | FolderResponse) => {
      if (item.type == 'folder' || item.type === 'file') {
        bloc.shareWithClient(item.id, item.type, !item.isClient).subscribe();
      }
    },
    [bloc],
  );

  const onClick = useCallback(
    (item: FileResponse | FolderResponse) => {
      if (item.type === 'folder') {
        setSearch('');
        setFolderId(item.id);
      } else {
        if (item.fileType === 'file') {
          window.open(item.url);
        } else if (item.fileType === 'document') {
          openDocumentEditorModal({ documentId: item.id.toString(), title: item.name, breadcrumbs });
        } else if (item.fileType === 'form') {
          opemFormViewModal({
            title: item.name,
            fileForm: item.form!,
          });
        } else if (item.fileType === 'presentation') {
          window.open(`/presentation/${item.objectId}/${orgId}/preview`);
        }
      }
    },
    [breadcrumbs, orgId],
  );

  const onDelete = useCallback(
    (item: FileResponse | FolderResponse) => {
      if (item.type === 'folder') {
        bloc.deleteFolder(item.id).subscribe();
      } else {
        bloc.deleteFile(item.id).subscribe();
      }
    },
    [bloc],
  );

  const onMove = useCallback(
    (item: FileResponse | FolderResponse) => {
      openMoveFiles({ orgId, id: item.id, type: item.type, onMove: () => bloc.getItems(orgId, folderId).subscribe() });
    },
    [bloc, folderId, orgId],
  );

  const onChangeOrg = useCallback((item: FileResponse | FolderResponse) => {
    openFileOrgModal({
      item: item,
    });
  }, []);

  const onVisibility = useCallback(
    (item: FileResponse | FolderResponse) => {
      if (item.type === 'folder') {
        bloc.changeFolderVisibility(item.id).subscribe();
      } else {
        bloc.changeFileVisibility(item.id).subscribe();
      }
    },
    [bloc],
  );

  const onShareUsers = useCallback(
    (item: FileResponse | FolderResponse) =>
      openSelectUsersModal({
        onlyStaff: false,
        title: 'Users',
        button: 'Update',
        placeholder: 'Users',
        userIds: item.users,
        required: false,
        onSelect: (userIds: UserId[]) => bloc.itemShareUsers(item.id, item.type, userIds),
      }),
    [bloc],
  );

  const onAddDocment = useCallback(() => {
    openGenericInputModal({
      title: 'Create Document',
      label: 'Document name',
      button: 'Create',
      onSubmit: name =>
        bloc.createDocument(orgId, name, folderId).pipe(
          tap(file => {
            openDocumentEditorModal({ documentId: file.id.toString(), title: file.name, breadcrumbs });
          }),
        ),
    });
  }, [bloc, orgId, folderId, breadcrumbs]);

  const onPermission = useCallback(
    (item: FileResponse | FolderResponse) =>
      openOrgFileShareModal({
        item: item,
      }),
    [],
  );

  useEffect(() => {
    bloc.getItems(orgId, folderId).subscribe();
  }, [bloc, folderId, orgId]);

  const onFormSelect = useCallback((item: FileResponse) => {
    opemFormViewModal({ title: item.name, fileForm: item.form! });
  }, []);

  const onBreadcrumb = useCallback((id: FolderId) => {
    setSearch('');
    setFolderId(id);
  }, []);

  const onDetail = useCallback(
    (item: FileResponse | FolderResponse) => {
      const iItem = item as FileResponse;
      if (iItem.objectId) {
        openPresentationSummaryModal({
          presentationId: iItem.objectId,
          orgId: orgId,
        });
      }
    },
    [orgId],
  );

  const addMenu = (
    <UncontrolledDropdown className="d-flex align-items-center">
      <DropdownToggle tag="a" className="text-muted hover-pointer">
        <i className="fa fa-plus-circle font-size-20 text-primary"></i>
      </DropdownToggle>
      <DropdownMenu right>
        <a className="dropdown-item" onClick={onAddFolder}>
          Add Folder
        </a>
        <a
          className="dropdown-item"
          onClick={() => openAddFiles({ orgId, folderId, users, clientPortal, onClose: () => bloc.getItems(orgId, folderId).subscribe() })}
        >
          Add Files
        </a>
        {/* Customer blueprint/design (PDF) — tagged so it can be told apart and used in proposals */}
        <a
          className="dropdown-item"
          onClick={() =>
            openAddFiles({
              orgId,
              folderId,
              users,
              clientPortal,
              fileType: 'design',
              accept: 'application/pdf',
              title: 'Add Design File',
              onClose: () => bloc.getItems(orgId, folderId).subscribe(),
            })
          }
        >
          Add Design File
        </a>
        <a className="dropdown-item" onClick={onAddDocment}>
          Add Document
        </a>
        {showForm && !clientPortal && (
          <a className="dropdown-item" onClick={() => openAddEditFormModal({ type: props.type, onSelect: onFormSelect, orgId, folderId })}>
            Add Form
          </a>
        )}
        {!clientPortal && (
          <>
            <a className="dropdown-item" onClick={() => openAddPresentationModal({ type: props.type, onSelect: onFormSelect, orgId, folderId })}>
              Add Presentation
            </a>
            <a
              className="dropdown-item"
              onClick={() =>
                openAttachSystemFilesModal({
                  folderId,
                  orgId,
                  onDone: () => bloc.getItems(orgId, folderId).subscribe(),
                  ids: items.map(i => i.systemFileId).filter(id => !!id) as string[],
                })
              }
            >
              Attach Customer Resource
            </a>
          </>
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );

  const content = (
    <Fragment>
      <div className="d-flex justify-content-between align-items-center">
        {!!customTilte && <CardTitle className="mb-0">{customTilte}</CardTitle>}
        <div className="d-flex align-items-center">
          {enableSearch && (
            <div className="search-box mr-2">
              <div className="position-relative">
                <input
                  value={search}
                  type="text"
                  className="form-control bg-light border-light rounded"
                  placeholder="Search..."
                  onChange={event => setSearch(event.target.value)}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </div>
          )}
          {clientPortal ? (
            addMenu
          ) : (
            <HasPermission module={'organizations'} moduleKey={'files'} action="add">
              {addMenu}
            </HasPermission>
          )}
        </div>
      </div>
      <hr className="mb-2" />
      <Row className={`${items.length + extraItems.length > 0 ? '' : 'd-flex justify-content-center'}`}>
        {!search && showBreadcrumb && (
          <Col sm={12}>
            <CBreadcrumb items={breadcrumbItems} />
          </Col>
        )}
        {!!search && (
          <Col sm={12}>
            <SearchList items={searchItems} onClick={onClick} onBreadcrumb={onBreadcrumb} />
          </Col>
        )}
        {!search && (
          <EmptySection
            style={{ marginTop: 50 }}
            icon="bx bx-file"
            items={[...extraItems, ...items]}
            title="No Files"
            description={`There is no files shared with you`}
          >
            {extraItems}
            {items.map((item, index) => (
              <FileItem
                users={users}
                user={user}
                key={index}
                item={item}
                onShare={() => onShare(item)}
                onPermission={() => onPermission(item)}
                onClick={() => onClick(item)}
                onRename={() => onRename(item)}
                onDelete={() => onDelete(item)}
                onDownload={() => item.type == 'folder' && openDownloadFolderModal({ folder: item, bloc })}
                onMove={() => onMove(item)}
                onChangeOrg={() => onChangeOrg(item)}
                onVisibility={() => onVisibility(item)}
                onShareUsers={() => onShareUsers(item)}
                onStatusChange={(id, status) => bloc.changeFolderStatus(id, status).subscribe()}
                onDetail={() => onDetail(item)}
                onShareWithClient={() => onShareWithClient(item)}
              />
            ))}
          </EmptySection>
        )}
      </Row>
    </Fragment>
  );

  if (card) {
    return (
      <Card>
        {/* A fixed-height card scrolls inside instead of letting tiles spill past its bottom edge */}
        <CardBody style={{ height: `${cardHeight ?? ''}`, ...(cardHeight ? { overflowY: 'auto' as const } : {}) }}>
          {content}
          <div style={{ marginBottom: 100 }}></div>
        </CardBody>
      </Card>
    );
  }
  return content;
});
